import { AgGridButtons } from "@/controls/buttons/AgGridButtons";
import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { ProcessData } from "@/pages/caster/models/ProcessData";
import { getMomentTz } from "@dexteel/mesf-core";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-enterprise";
import { ManualAutoButton } from "../../../buttons/ManualAutoButton";

export interface IProbeRow {
	id: string;
	type: "liquidus" | "ladle" | "probe";
	label: string;
	timestamp: Date | null;
	temperature: number | null;
	superHeat: string;
	isManual: boolean;
	trimProbeOxygen?: number | null;
}
interface Props {
	processData: ProcessData | null;
	onEditButtonClick: (row: IProbeRow) => void;
	onDeleteButtonClick: (row: IProbeRow) => void;
	onDownloadButtonClick: (row: IProbeRow) => void;
}
const moment = getMomentTz();

export const useProbesTableData = ({
	processData,
	onEditButtonClick,
	onDeleteButtonClick,
	onDownloadButtonClick,
}: Props) => {
	const { state } = useCastersContext();
	const probes = state.probes || [];

	const rowData: IProbeRow[] = [];

	rowData.push({
		id: "liquidus",
		type: "liquidus",
		label: "Liquid",
		timestamp: null,
		temperature: processData ? processData.F1LiquidusTemperature : null,
		superHeat: "--",
		isManual: false,
	});

	rowData.push({
		id: "ladle",
		type: "ladle",
		label: "Ladle",
		timestamp: processData ? processData.TrimProbeTime : null,
		temperature: processData ? processData.TrimProbeTemperature : null,
		trimProbeOxygen: processData ? processData.TrimProbeOxygen : null,
		superHeat:
			processData && processData.LadleSuperHeat !== null
				? String(processData.LadleSuperHeat)
				: "--",
		isManual: processData ? !!processData.IsTrimDataManual : false,
	});

	probes.forEach((probe) => {
		rowData.push({
			id: probe.ProbeId?.toString(),
			type: "probe",
			label: ``,
			timestamp: probe.Timestamp,
			temperature: probe.Temperature,
			superHeat: probe.SuperHeat !== null ? String(probe.SuperHeat) : "--",
			isManual: !!probe.IsProbeManual,
		});
	});

	const columns: ColGroupDef[] = [
		{
			headerName: "Probes",
			children: [
				{
					field: "manual-auto",
					headerName: " ",
					flex: 1,
					minWidth: 39,
					maxWidth: 39,
					cellRenderer: (params: ICellRendererParams) => {
						return <ManualAutoButton isManual={params.data.isManual} />;
					},
				},
				{
					field: "label",
					headerName: "",
					flex: 1,
					minWidth: 62,
					cellStyle: {
						textAlign: "left",
					},
				},
				{
					field: "timestamp",
					headerName: "Time",
					flex: 1,
					minWidth: 60,
					valueGetter: (params: any) => {
						if (params.data.timestamp) {
							return moment(params.data.timestamp).format("HH:mm");
						}
						return "--";
					},
				},
				{
					field: "temperature",
					headerName: "Temp",
					flex: 1,
					minWidth: 58,
				},
				{
					field: "superHeat",
					headerName: "SH",
					flex: 1,
					minWidth: 50,
					cellStyle: {
						textAlign: "right",
					},
				},
				{
					field: "trimProbeOxygen",
					headerName: "O2",
					flex: 1,
					minWidth: 50,
					cellStyle: {
						textAlign: "right",
					},
				},
				{
					field: "actions",
					headerName: " ",
					flex: 1,
					minWidth: 80,
					maxWidth: 80,
					cellRenderer: (params: ICellRendererParams) => {
						if (params.data.type === "liquidus") {
							return null;
						} else if (params.data.type === "ladle") {
							return (
								<AgGridButtons
									editItem={() => onEditButtonClick(params.data)}
									downloadItem={() => onDownloadButtonClick(params.data)}
									downloadLabel="Get From PLC"
								/>
							);
						} else {
							return (
								<AgGridButtons
									editItem={() => onEditButtonClick(params.data)}
									deleteItem={() => onDeleteButtonClick(params.data)}
									downloadLabel="Get From PLC"
								/>
							);
						}
					},
				},
			],
		},
	];

	const defaultColDef: ColDef = {
		filter: false,
		floatingFilter: false,
		sortable: false,
		resizable: false,
		editable: false,
	};

	return { columns, defaultColDef, rowData };
};
